import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { filter, switchMap, tap, Subject, takeUntil, finalize, catchError, of } from 'rxjs';
import { ContactDetailsFormService } from '../../../services/contact-details-form.service';
import { AccountContactStatusDesc } from '../../../interfaces/account-contact-status-desc.interface';
import { AccountContactUpdate } from '../../../interfaces/account-contact-update.interface';
import { AccountContactAdd } from '../../../interfaces/account-contact-add.interface';
import { DialogService } from '../../../services/dialog.service';
import { RegistrationDetailsService } from '../../../services/registration-details.service';
import { SpinnerModule } from 'common';
import { NotificationService } from '../../../services/notification.service';
import { MatTooltipModule } from '@angular/material/tooltip';

export enum ContactDetailsDialogMode {
  Edit,
  Add,
}
export interface ContactDetailsDialogData {
  accountId: number;
  contactId?: number;
  mode: ContactDetailsDialogMode;
}

@Component({
  selector: 'si-contact-details-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatButtonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, SpinnerModule, MatTooltipModule],
  templateUrl: './contact-details-dialog.component.html',
  styleUrl: './contact-details-dialog.component.scss',
})
export class ContactDetailsDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  dialogRef = inject(MatDialogRef<ContactDetailsDialogComponent>);
  data = inject<ContactDetailsDialogData>(MAT_DIALOG_DATA);
  registrationDetailsService = inject(RegistrationDetailsService);
  formService = inject(ContactDetailsFormService);
  dialogService = inject(DialogService);
  notificationService = inject(NotificationService);

  public isFetching = false;
  public isSaving = false;

  get form() {
    return this.formService.form;
  }

  get contactActive() {
    return this.form.get('status').value === AccountContactStatusDesc.Active;
  }

  get contactPrimary() {
    return this.form.get('isPrimaryContact').value;
  }

  get editMode() {
    return this.data.mode === ContactDetailsDialogMode.Edit;
  }

  get addMode() {
    return this.data.mode === ContactDetailsDialogMode.Add;
  }

  ngOnInit() {
    this.formService.init();

    if (this.editMode) {
      this.isFetching = true;
      this.registrationDetailsService
        .getContactDetails$(this.data.accountId, this.data.contactId)
        .pipe(
          tap((data) => this.formService.initFromContactDetails(data)),
          takeUntil(this.destroy$),
          finalize(() => this.isFetching = false),
        )
        .subscribe();
    }
  }

  handleToggleStatus() {
    this.dialogService
      .openConfirmation()
      .afterClosed()
      .pipe(
        filter((confirmed: boolean) => confirmed),
        switchMap(() => {
          return this.registrationDetailsService.updateContactStatus$(this.data.accountId, this.data.contactId, !this.contactActive);
        }),
        tap(() => {
          this.form.controls.status.setValue(this.contactActive ? AccountContactStatusDesc.Inactive : AccountContactStatusDesc.Active);
          this.dialogRef.close(true);
        }),
        tap(() => this.notificationService.showNotification({ message: `Contact ${this.contactActive ? 'activated' : 'deactivated'}` })),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  handleSetAsPrimary() {
    const content = 'Setting this contact as primary will automatically remove the primary status from any other contact. Do you want to continue?';

    this.dialogService
      .openConfirmation({ content })
      .afterClosed()
      .pipe(
        filter((confirmed: boolean) => confirmed),
        switchMap(() => {
          return this.registrationDetailsService.setContactAsPrimary$(this.data.accountId, this.data.contactId);
        }),
        tap(() => {
          this.form.controls.isPrimaryContact.setValue(true);
          this.dialogRef.close(true);
        }),
        tap(() => this.notificationService.showNotification({ message: 'Contact set as primary' })),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  handleSubmit() {
    if (this.form.invalid) {
      return;
    }

    const content =
      this.addMode && this.contactPrimary
        ? 'Changes will be applied, including setting this contact as primary. This will make any other primary contact a non-primary. Do you want to proceed?'
        : undefined;

    this.dialogService
      .openConfirmation({ content })
      .afterClosed()
      .pipe(
        filter((confirmed: boolean) => confirmed),
        tap(() => this.isSaving = true),
        switchMap(() => {
          if (this.editMode) {
            const data: AccountContactUpdate = {
              ...this.form.getRawValue(),
              accountID: this.data.accountId,
              accountContactID: this.data.contactId,
            } as AccountContactUpdate;
            return this.registrationDetailsService.updateContactDetails$(data).pipe(
              tap(() => this.notificationService.showNotification({ message: 'Contact details updated' }))
            );
          }

          if (this.addMode) {
            const data: AccountContactAdd = {
              ...this.form.value,
              accountID: this.data.accountId,
            } as AccountContactAdd;
            return this.registrationDetailsService.addContactDetails$(data).pipe(
              tap(() => this.notificationService.showNotification({ message: 'Contact added' }))
            );
          }
        }),
        tap(() => {
          this.isSaving = false;
          this.dialogRef.close(true);
        }),
        catchError(() => {
          this.isSaving = false;
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
